'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, FileText, CreditCard, Trash2 } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { salesInvoices, customers } from '../data/mock-data';
import { INVOICE_STATUS_LABELS, INVOICE_STATUS_COLORS } from '../types';
import type { SalesInvoice, InvoiceStatus } from '../types';
import { formatCurrency } from '../../../types';

// 청구서 발행 모달
function InvoiceAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [items, setItems] = useState([{ itemName: '', quantity: 1, unitPrice: 0 }]);

  const addItem = () => {
    setItems([...items, { itemName: '', quantity: 1, unitPrice: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="청구서 발행"
      description="새로운 판매 청구서를 발행합니다."
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button variant="outline">미리보기</Button>
          <Button onClick={onClose}>발행</Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* 기본 정보 */}
        <div className="grid grid-cols-2 gap-4">
          <FormField label="청구번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="고객" required>
            <FormSelect>
              <option value="">고객을 선택하세요</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="주문서 참조">
            <FormSelect>
              <option value="">선택 안함</option>
              <option value="so-001">SO-2024-001</option>
              <option value="so-002">SO-2024-002</option>
            </FormSelect>
          </FormField>
          <FormField label="만기일" required>
            <FormInput type="date" />
          </FormField>
        </div>

        {/* 품목 목록 */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">청구 품목</h3>
            <Button variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-4 h-4 mr-1" />
              품목 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">품목명</th>
                  <th className="text-right p-3 font-medium w-24">수량</th>
                  <th className="text-right p-3 font-medium w-32">단가</th>
                  <th className="text-right p-3 font-medium w-32">금액</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2">
                      <FormInput placeholder="품목명" />
                    </td>
                    <td className="p-2">
                      <FormInput type="number" min={1} defaultValue={1} className="text-right" />
                    </td>
                    <td className="p-2">
                      <FormInput type="number" min={0} defaultValue={0} className="text-right" />
                    </td>
                    <td className="p-2 text-right font-medium">
                      ₩0
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => removeItem(index)}
                        className="p-1 rounded hover:bg-accent"
                        disabled={items.length === 1}
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 금액 정보 */}
        <div className="flex justify-end">
          <div className="w-64 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">소계</span>
              <span>₩0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">부가세 (10%)</span>
              <span>₩0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">할인</span>
              <FormInput type="number" min={0} defaultValue={0} className="w-24 text-right h-8" />
            </div>
            <div className="flex justify-between pt-2 border-t border-border font-bold text-base">
              <span>청구 금액</span>
              <span>₩0</span>
            </div>
          </div>
        </div>

        {/* 결제 정보 */}
        <div className="p-4 bg-muted/30 rounded-lg space-y-4">
          <h3 className="font-medium">결제 정보</h3>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="결제 조건">
              <FormSelect>
                <option value="immediate">즉시 결제</option>
                <option value="net30">30일 후 결제</option>
                <option value="net60">60일 후 결제</option>
                <option value="eom">월말 결제</option>
              </FormSelect>
            </FormField>
            <FormField label="결제 방법">
              <FormSelect>
                <option value="bank">계좌이체</option>
                <option value="card">카드결제</option>
                <option value="cash">현금</option>
                <option value="check">수표</option>
              </FormSelect>
            </FormField>
          </div>
        </div>

        {/* 비고 */}
        <FormField label="비고">
          <FormTextarea rows={2} placeholder="청구서 관련 메모를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function InvoiceListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<InvoiceStatus | 'all'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return salesInvoices.filter((invoice) => {
      // 검색 필터
      const matchesSearch =
        invoice.invoiceNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.customerName.toLowerCase().includes(searchQuery.toLowerCase());

      // 상태 필터
      const matchesStatus =
        selectedStatus === 'all' || invoice.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, selectedStatus]);

  const columns = [
    {
      key: 'invoiceNo',
      label: '청구번호',
      width: '12%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm">{value as string}</span>
      ),
    },
    {
      key: 'customerName',
      label: '고객',
      width: '14%',
      sortable: true,
      render: (value: unknown) => (
        <span className="truncate block">{value as string}</span>
      ),
    },
    {
      key: 'total',
      label: '청구금액',
      width: '12%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="font-medium whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'paidAmount',
      label: '결제금액',
      width: '12%',
      align: 'right' as const,
      render: (value: unknown) => (
        <span className="text-green-500 whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'outstandingAmount',
      label: '미수금',
      width: '12%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => {
        const amount = value as number;
        return (
          <span className={`whitespace-nowrap ${amount > 0 ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
            {formatCurrency(amount)}
          </span>
        );
      },
    },
    {
      key: 'dueDate',
      label: '만기일',
      width: '10%',
      sortable: true,
      render: (value: unknown, row: SalesInvoice) => {
        const date = new Date(value as string);
        const isOverdue = row.status === 'overdue';
        return (
          <span className={`whitespace-nowrap ${isOverdue ? 'text-red-500' : ''}`}>
            {date.toLocaleDateString('ko-KR')}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: '상태',
      width: '8%',
      render: (value: unknown) => {
        const status = value as InvoiceStatus;
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${INVOICE_STATUS_COLORS[status]}`}>
            {INVOICE_STATUS_LABELS[status]}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '10%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="상세보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="결제등록">
            <CreditCard className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="인쇄">
            <FileText className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Partial<Record<InvoiceStatus, number>> = {};
    const statusOrder: InvoiceStatus[] = ['unpaid', 'partial', 'paid', 'overdue'];
    statusOrder.forEach((status) => {
      stats[status] = salesInvoices.filter((inv) => inv.status === status).length;
    });
    return stats;
  }, []);

  // 요약 정보
  const totalInvoiced = salesInvoices.reduce((sum, inv) => sum + inv.total, 0);
  const totalPaid = salesInvoices.reduce((sum, inv) => sum + inv.paidAmount, 0);
  const totalOutstanding = salesInvoices.reduce((sum, inv) => sum + inv.outstandingAmount, 0);
  const overdueCount = salesInvoices.filter((inv) => inv.status === 'overdue').length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="청구서 관리"
        description="판매 청구서를 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            청구서 발행
          </button>
        }
      />

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 청구금액</div>
          <div className="text-2xl font-bold mt-1">{formatCurrency(totalInvoiced)}</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 수금액</div>
          <div className="text-2xl font-bold mt-1 text-green-500">{formatCurrency(totalPaid)}</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 미수금</div>
          <div className="text-2xl font-bold mt-1 text-yellow-500">{formatCurrency(totalOutstanding)}</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">연체 청구서</div>
          <div className="text-2xl font-bold mt-1 text-red-500">{overdueCount}건</div>
        </div>
      </div>

      {/* 상태별 필터 버튼 */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedStatus('all')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            selectedStatus === 'all'
              ? 'bg-primary text-primary-foreground'
              : 'bg-card border border-border hover:bg-accent'
          }`}
        >
          전체 ({salesInvoices.length})
        </button>
        {(['unpaid', 'partial', 'paid', 'overdue'] as InvoiceStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(status)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedStatus === status
                ? 'bg-primary text-primary-foreground'
                : 'bg-card border border-border hover:bg-accent'
            }`}
          >
            {INVOICE_STATUS_LABELS[status]} ({statusStats[status] || 0})
          </button>
        ))}
      </div>

      {/* 검색 */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="청구번호, 고객명 검색..."
          className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 청구서 발행 모달 */}
      <InvoiceAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
